import { useEffect, useRef } from "react";
import { apiFetch } from "../lib/api";

const HEARTBEAT_MS = 60_000;
const LONG_RUN_MS = 8 * 60 * 60 * 1000;
const WARN_COOLDOWN_MS = 5 * 60 * 1000;

/**
 * M67: keep server last_heartbeat_at fresh while a web timer runs.
 * Also warns once when elapsed ≥ 8h and sets beforeunload when running.
 */
export function useTimerHeartbeat(activeTimer) {
  const longRunWarned = useRef(false);
  const lastFailWarnAt = useRef(0);

  useEffect(() => {
    longRunWarned.current = false;
  }, [activeTimer?.timer_id]);

  useEffect(() => {
    if (!activeTimer?.timer_id) return undefined;
    let active = true;

    const ping = (keepalive = false) => {
      apiFetch("/timer/heartbeat", { method: "POST", keepalive })
        .then((res) => {
          if (!active) return;
          // audit-174 G04: server auto-stopped stale timer — clear client state
          if (res.status === 404) {
            window.dispatchEvent(new CustomEvent("tymlogr:timer-cleared", {
              detail: { reason: "stale-or-missing" },
            }));
            return;
          }
          if (!res.ok) {
            const now = Date.now();
            if (now - lastFailWarnAt.current >= WARN_COOLDOWN_MS) {
              lastFailWarnAt.current = now;
              window.dispatchEvent(
                new CustomEvent("tymlogr:timer-heartbeat-fail", {
                  detail: { status: res.status },
                })
              );
            }
          }
        })
        .catch(() => {});
    };

    ping(false);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") ping(false);
    }, HEARTBEAT_MS);

    const onVisibility = () => {
      if (document.visibilityState === "visible") ping(false);
      else ping(true);
    };

    const onPageHide = () => ping(true);

    const onBeforeUnload = (e) => {
      ping(true);
      e.preventDefault();
      e.returnValue = "";
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [activeTimer?.timer_id]);

  useEffect(() => {
    if (!activeTimer?.start_time) return undefined;
    const tick = () => {
      const start = new Date(activeTimer.start_time).getTime();
      if (!Number.isFinite(start)) return;
      if (Date.now() - start >= LONG_RUN_MS && !longRunWarned.current) {
        longRunWarned.current = true;
        window.dispatchEvent(
          new CustomEvent("tymlogr:timer-long-run", {
            detail: { hours: 8 },
          })
        );
      }
    };
    tick();
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, [activeTimer?.start_time, activeTimer?.timer_id]);
}
